import React, { useEffect, useRef } from 'react';
import './AdSense.css';

/**
 * Google AdSense Component
 * 
 * @param {string} adSlot - The ad slot ID from AdSense
 * @param {string} adFormat - Ad format: 'auto', 'horizontal', 'rectangle', 'vertical'
 * @param {boolean} fullWidth - Whether the ad should be full width responsive
 * @param {string} className - Additional CSS classes
 */
const AdSense = ({
    adSlot = '',
    adFormat = 'auto',
    fullWidth = true,
    className = ''
}) => {
    const adRef = useRef(null);
    const isAdLoaded = useRef(false);

    // Don't render ads on development or local environments
    const isDev = process.env.NODE_ENV !== 'production' || 
                  window.location.hostname === 'localhost' || 
                  window.location.hostname === '127.0.0.1' ||
                  window.location.hostname.startsWith('192.168.');

    useEffect(() => {
        // Only push ad if not already loaded, adSlot is provided, and not in dev
        if (adRef.current && !isAdLoaded.current && adSlot && !isDev) {
            try {
                // Check if adsbygoogle is available
                if (window.adsbygoogle) {
                    (window.adsbygoogle = window.adsbygoogle || []).push({});
                    isAdLoaded.current = true;
                }
            } catch (error) {
                // Silently swallow AdSense TagError (like availableWidth=0) 
                // to prevent it from cluttering the console in edge cases.
            }
        }
    }, [adSlot, isDev]);

    // Don't render if no ad slot provided or in development
    if (!adSlot || isDev) {
        return null;
    }

    return (
        <div className={`adsense-container ${className}`}>
            <ins
                ref={adRef}
                className="adsbygoogle"
                style={{ display: 'block' }}
                data-ad-client="ca-pub-4190589715489043"
                data-ad-slot={adSlot}
                data-ad-format={adFormat}
                data-full-width-responsive={fullWidth ? 'true' : 'false'}
            />
        </div>
    );
};

/**
 * Predefined ad formats for common placements
 */
export const AdBanner = ({ adSlot, className }) => (
    <AdSense
        adSlot={adSlot}
        adFormat="horizontal"
        className={`ad-banner ${className || ''}`}
    />
);

export const AdRectangle = ({ adSlot, className }) => (
    <AdSense
        adSlot={adSlot}
        adFormat="rectangle"
        fullWidth={false}
        className={`ad-rectangle ${className || ''}`}
    />
);

export const AdInFeed = ({ adSlot, className }) => (
    <AdSense
        adSlot={adSlot}
        adFormat="fluid"
        className={`ad-in-feed ${className || ''}`}
    />
);

export default AdSense;
